import React, { useEffect, useRef } from 'react';

export default function InteractiveCursorBackground() {
  const canvasRef = useRef(null);
  const mousePos = useRef({ x: -1000, y: -1000 });
  const animationRef = useRef(null);
  const hexagonsRef = useRef([]);
  const pulsesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // --- PULSE CLASS (Reduced to 1/4 Speed) ---
    class Pulse {
      constructor(w, h) {
        this.reset(w, h);
      }

      reset(w, h) {
        this.x = Math.random() * w;
        this.y = Math.random() * h;
        // Speed reduced from ~1.5-3.5 range to ~0.37-0.87 range (1/4 speed)
        this.speed = 0.37 + Math.random() * 0.5;
        
        const directions = [0, Math.PI / 3, (2 * Math.PI) / 3, Math.PI, (4 * Math.PI) / 3, (5 * Math.PI) / 3];
        this.angle = directions[Math.floor(Math.random() * directions.length)];
        this.velocity = {
          x: Math.cos(this.angle) * this.speed,
          y: Math.sin(this.angle) * this.speed
        };
        this.life = 0;
        // Increased maxLife so they don't disappear too quickly at slow speeds
        this.maxLife = 400 + Math.random() * 600;
      }

      update(w, h) {
        this.x += this.velocity.x;
        this.y += this.velocity.y;
        this.life++;

        // Change direction occasionally
        if (this.life % 120 === 0) { 
          const change = (Math.random() > 0.5 ? 1 : -1) * (Math.PI / 3);
          this.angle += change;
          this.velocity.x = Math.cos(this.angle) * this.speed;
          this.velocity.y = Math.sin(this.angle) * this.speed;
        }

        if (this.life > this.maxLife || this.x < -50 || this.x > w + 50 || this.y < -50 || this.y > h + 50) {
          this.reset(w, h);
        }
      }
    }

    // --- HEXAGON CLASS ---
    class Hexagon {
      constructor(x, y, size) {
        this.x = x;
        this.y = y;
        this.size = size;
        this.glow = 0;
        this.pulseGlow = 0;
      }

      update(mx, my, pulses) {
        const dx = mx - this.x;
        const dy = my - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        // --- 1/2 MOUSE EFFECT ---
        // Reduced radius from 120 to 80 and peak glow from 1.0 to 0.5
        if (dist < 80) {
          this.glow = 0.5 - (dist / 160); 
        } else {
          this.glow *= 0.92;
        }

        this.pulseGlow *= 0.96;
        pulses.forEach(p => {
          const pdx = p.x - this.x;
          const pdy = p.y - this.y;
          const pDist = Math.sqrt(pdx * pdx + pdy * pdy);
          if (pDist < 45) {
            this.pulseGlow = Math.max(this.pulseGlow, 0.7);
          }
        });
      }

      draw(ctx) {
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i;
          const x = this.x + this.size * Math.cos(angle);
          const y = this.y + this.size * Math.sin(angle);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        
        // Alpha calculation adjusted for the more subtle mouse glow
        const alpha = 0.04 + (this.glow * 0.4) + (this.pulseGlow * 0.25);
        
        ctx.strokeStyle = `rgba(34, 211, 238, ${alpha})`;
        ctx.lineWidth = 0.8 + (this.glow * 1.5) + (this.pulseGlow * 1.5);
        ctx.stroke();
        
        if (this.glow > 0.05 || this.pulseGlow > 0.5) {
          const fillAlpha = (this.glow * 0.08) + (this.pulseGlow * 0.04);
          ctx.fillStyle = `rgba(34, 211, 238, ${fillAlpha})`;
          ctx.fill();
        }
      }
    }

    const initGrid = () => {
      hexagonsRef.current = [];
      const size = 25;
      const width = size * 2;
      const height = Math.sqrt(3) * size;
      
      for (let row = 0; row < canvas.height / height + 2; row++) {
        for (let col = 0; col < canvas.width / (width * 0.75) + 2; col++) {
          const x = col * (width * 0.75);
          const y = row * height + (col % 2) * (height / 2);
          hexagonsRef.current.push(new Hexagon(x, y, size));
        }
      }

      pulsesRef.current = Array.from({ length: 12 }, () => new Pulse(canvas.width, canvas.height));
    };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initGrid();
    };

    const animate = () => {
      ctx.fillStyle = '#0a0e1a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      pulsesRef.current.forEach(p => p.update(canvas.width, canvas.height));

      hexagonsRef.current.forEach(hex => {
        hex.update(mousePos.current.x, mousePos.current.y, pulsesRef.current);
        hex.draw(ctx);
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', resize);
    
    resize();
    animate();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -1,
        display: 'block',
        pointerEvents: 'none',
        background: '#0a0e1a'
      }}
    />
  );
}